'use client';
import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { usePathname, useSearchParams } from 'next/navigation';
import { updateSearchParams } from '@overlapdev/functions';
import { Button } from '@overlapdev/elements';
import Link from 'next/link';
import { LoadingSVG } from '@overlapdev/elements/assets';
import { ImageType, PageType } from '@overlapdev/types';
import { LoadMoreSVG } from 'components/svg';

type PostType = PageType & { grid_image?: ImageType };

const SiteSearchResults = ({ posts_per_page }: { posts_per_page: string }) => {
	//__Hooks
	const pathname = usePathname();
	const searchParams = useSearchParams();

	//__State
	const [loaded, setLoaded] = useState(false);
	const [page, setPage] = useState(1);
	const [results, setResults] = useState<Array<PostType>>([]);
	const [search, setSearch] = useState<string | null>('');
	const [total, setTotal] = useState(0);
	const [updating, setUpdating] = useState(false);

	//__Functions
	const update = async (offset: number, search: string | null, amount?: number) => {
		setUpdating(true);
		const query: any = {
			amount,
			offset,
			posts_per_page,
			search: search || ''
		};
		const updated = await fetch(`/api/site-search?query=${JSON.stringify(query)}`).then((res) => res.json());

		if (offset === 0) {
			//Reset results
			setResults(updated?.posts);
		} else {
			//Append new results to existing results
			if (updated?.posts?.length) {
				setResults((results) => [...results, ...updated?.posts]);
			}
		}
		setTotal(updated.total || 0);
		setUpdating(false);
	};

	const updatePage = () => {
		const new_page = page + 1;
		if (new_page.toString() !== searchParams.get('p')) {
			window.history.pushState(null, '', `${pathname}?${updateSearchParams(searchParams.toString(), 'p', new_page === 1 ? '' : new_page)}`);
		}
	};

	//__Use Effect
	useEffect(() => {
		const page_param = searchParams.has('p') ? parseInt(searchParams.get('p') || '', 10) : 1;
		const search_param = searchParams.has('search') ? searchParams.get('search') : '';

		if (!loaded) {
			const amount = page_param * parseInt(posts_per_page, 10);
			update(0, search_param, amount);
		} else {
			//If search or filters or date are changed
			if (search_param !== search) {
				update(0, search_param);
			} else if (page_param !== page) {
				const offset = (page_param - 1) * parseInt(posts_per_page, 10);
				update(offset, search_param);
			}
		}

		setSearch(search_param);
		setPage(page_param);
		setLoaded(true);
	}, [searchParams]);

	return (
		<div className='relative'>
			<div className='font-semibold my-4 text-right'>{total} Results</div>
			<div className='mb-14 mt-6 md:mb-32 md:mt-16'>
				{updating ? (
					<div className='absolute flex justify-center top-0 w-full'>
						<LoadingSVG width={200} fill='#373737' />
					</div>
				) : null}
				{results?.length ? (
					<div>
						<h4 className='mb-6 text-grey md:mb-10'>
							<span>Results for</span> <span className='text-black'>{search}</span>
						</h4>
						<div className='flex flex-col gap-5'>
							{results.map((item: PostType) => {
								const { excerpt, id, post_title, relative_path } = item || {};

								return (
									<div key={`site_search_${id}`} className='border border-light-grey flex flex-col gap-4 p-8 relative rounded-3xl transition-ease md:p-10 hover:border-transparent hover:shadow-[30px_35px_55px_0px_rgba(46,46,46,0.23)]'>
										<h6>{parse(post_title)}</h6>
										{excerpt ? <div>{parse(excerpt)}</div> : ''}
										<Link href={relative_path} className='absolute top-0 left-0 w-full h-full'>
											<span className='hidden'>{post_title}</span>
										</Link>
									</div>
								);
							})}
						</div>
					</div>
				) : null}
				{results?.length < total ? (
					<div className='mt-10 text-center md:mt-16'>
						<Button className='arrow_link cursor-pointer no_animate' link={{ title: 'Load More', url: '' }} onClick={() => updatePage()}>
							<LoadMoreSVG className='text-black' width={20} />
						</Button>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default SiteSearchResults;
